/* Входы формы «Рилса» ↔ вид экрана шаблона (план рилса §2.4; чистые функции, без DOM и запросов).
   Вид — то, что из состояния движка уходит в заказ: режим (one · multi · pack · app), шаблон или набор шаблонов, кто какую роль играет
   и id загрузок фото предмета по видам. Ключи — только опубликованные формой (form-keys: поля шаблона и контракт экрана):
   - режим и выбор: `mode` (только значение, которое ключ предлагает), `template_id` или `template_ids` (набор — в порядке отметки);
   - состав: JSON-ключ `cast` контракта экрана [{role_id, who, avatar_id?, person_id?, skin_id?, templates?}] или строковые поля `cast_<роль>`
     (ссылки self | user_avatar:ID | person:ID | character:ID через запятую) — тот же разбор, что у родной формы (reels-cast.ts);
     вид персонажа STEOS (`skin_id`) — только у JSON-ключа, когда контракт публикует это свойство (castSkins);
   - фото предмета: JSON-ключ `objects` {goods, food, place, pics: [id загрузок]} или поля `<вид>_<N>` (upload photo).
   Режим из входов — то же правило, что у родной формы (ReelsOrderForm: выбранный → сцена шаблона → default_value → по ключам). */
import type { StudioForm } from '../studio/studio-api';
import { entryRef, refEntry } from '../studio/engine-kit/people-bridge';
import { record, rows } from '../studio/studio-content';
import { keyInfo, patchInputs, readKey, readList, readText } from './form-keys';
import { JSON_CAST } from './reels-cast';
import { objectKind, reelsPolicy, type ReelsMode, type ReelsPolicy } from './reels-policy';

type Inputs = Record<string, string>;
type Patch = string | string[] | Record<string, unknown> | unknown[] | null;
export const MODES: readonly ReelsMode[] = ['one', 'multi', 'pack', 'app'];
export const OBJECT_KINDS = ['goods', 'food', 'place', 'pics'] as const;
const CAST_FIELD = /^cast_([a-zA-Z][a-zA-Z0-9_]{0,31})$/;
const UPLOAD_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Исполнитель роли: role — id роли формы, ref — ссылка состава, templates — шаблоны набора, где роль играет,
    skin — вид персонажа STEOS (id вида; нет — вид по умолчанию) */
export interface CastLine {
  role: string;
  ref: string;
  templates: string[];
  skin?: number;
}
export interface ReelsView {
  mode: ReelsMode;
  /** Шаблон режима one ('' — не выбран) */
  id: string;
  /** Шаблоны набора в порядке отметки */
  ids: string[];
  cast: CastLine[];
  /** id загрузок фото предмета по видам (порядок — порядок лотка) */
  objects: Record<string, string[]>;
}

/** Режим, который ключ `mode` формы предлагает; нет ключа или значения — null (в заказ не уходит) */
export function offers(form: StudioForm | null | undefined, value: ReelsMode): ReelsMode | null {
  const field = keyInfo(form, 'mode');
  return field && (!field.values.length || field.values.includes(value)) ? value : null;
}
/** Форма позволяет набор шаблонов (ключ template_ids и режим multi) */
export const canMulti = (form: StudioForm | null | undefined) => !!keyInfo(form, 'template_ids') && (!keyInfo(form, 'mode') || offers(form, 'multi') === 'multi');
/** Форма позволяет пакет (режим pack и offer.pack) */
export const canPack = (form: StudioForm | null | undefined, policy: ReelsPolicy) => !!policy.pack && offers(form, 'pack') === 'pack';
/** Допустимые шаблоны формы (values ключа template_id или template_ids); пусто — любые шаблоны политики */
export function allowedIds(form: StudioForm | null | undefined): string[] {
  return keyInfo(form, 'template_id')?.values ?? keyInfo(form, 'template_ids')?.values ?? [];
}

/** Режим по входам — правило родной формы */
export function modeOf(form: StudioForm, inputs: Inputs, policy: ReelsPolicy = reelsPolicy(form)): ReelsMode {
  const modeField = keyInfo(form, 'mode');
  const idField = keyInfo(form, 'template_id');
  const idsField = keyInfo(form, 'template_ids');
  const chosen = readText(modeField, inputs);
  const ids = readList(idsField, inputs);
  const known = (value: string): value is ReelsMode =>
    !!modeField && (modeField.values.length ? modeField.values.includes(value) : (MODES as readonly string[]).includes(value));
  const fallback: ReelsMode = idsField && ids.length > 1 ? 'multi' : idField || idsField ? 'one' : 'app';
  const preset = modeField?.defaultValue ?? '';
  if (known(chosen)) return chosen;
  return policy.mode ?? (known(preset) && (preset !== 'one' || !!policy.templates.length || !!idField) ? preset : fallback);
}

/** Контракт состава принимает вид персонажа: JSON-ключ `cast` публикует свойство `skin_id` (docs/61 §4) */
export function castSkins(form: StudioForm | null | undefined): boolean {
  const info = keyInfo(form, JSON_CAST);
  if (!form || !info?.json) return false;
  const field = rows(record(form).screen_fields).find(f => f.key === JSON_CAST);
  /* сервер публикует свойства списком {key, …}; раскладка стенда — объектом по имени */
  const props = field?.properties;
  return Array.isArray(props) ? rows(props).some(p => p.key === 'skin_id') : 'skin_id' in record(props);
}
const skinOf = (value: unknown) => (typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : undefined);

/** Состав из входов: JSON `cast` или поля `cast_<роль>` (ссылки по формату, без дублей внутри роли) */
export function castOf(form: StudioForm, inputs: Inputs): CastLine[] {
  const info = keyInfo(form, JSON_CAST);
  if (info?.json) {
    const raw = readKey(info, inputs);
    return (Array.isArray(raw) ? raw : []).flatMap(entry => {
      const e = record(entry);
      const ref = entryRef(e);
      const role = typeof e.role_id === 'string' ? e.role_id : '';
      if (!ref || !role) return [];
      const templates = Array.isArray(e.templates) ? e.templates.filter((x): x is string => typeof x === 'string') : [];
      const skin = e.who === 'char' ? skinOf(e.skin_id) : undefined;
      return [skin ? { role, ref, templates, skin } : { role, ref, templates }];
    });
  }
  return form.fields.flatMap(field => {
    const m = CAST_FIELD.exec(field.key);
    if (!m || field.type !== 'cast') return [];
    const refs = [...new Set((inputs[field.key] ?? '').split(',').map(x => x.trim()).filter(Boolean))];
    return refs.flatMap(ref => (refEntry(m[1], ref) ? [{ role: m[1], ref, templates: [] }] : []));
  });
}

/** Поля фото предмета формы по видам, в порядке полей (`goods_1`, `goods_2`… → goods) */
export function objectFields(form: StudioForm | null | undefined): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const field of form?.fields ?? [])
    if (field.upload?.kind === 'photo') {
      const kind = objectKind(field.key);
      (out[kind] ??= []).push(field.key);
    }
  return out;
}
/** Фото предмета из входов: JSON `objects` или поля по видам */
export function objectsOf(form: StudioForm, inputs: Inputs): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  const info = keyInfo(form, 'objects');
  if (info?.json) {
    const raw = record(readKey(info, inputs));
    for (const kind of OBJECT_KINDS) {
      const ids = Array.isArray(raw[kind]) ? (raw[kind] as unknown[]).filter((x): x is string => typeof x === 'string' && UPLOAD_ID.test(x)) : [];
      if (ids.length) out[kind] = ids;
    }
  }
  for (const [kind, keys] of Object.entries(objectFields(form))) {
    const ids = keys.map(key => inputs[key]).filter((x): x is string => !!x && UPLOAD_ID.test(x));
    if (ids.length) out[kind] = [...(out[kind] ?? []), ...ids];
  }
  return out;
}

/** Вид экрана по входам формы */
export function viewOf(form: StudioForm, inputs: Inputs, policy: ReelsPolicy = reelsPolicy(form)): ReelsView {
  const mode = modeOf(form, inputs, policy);
  const id = readText(keyInfo(form, 'template_id'), inputs);
  const ids = readList(keyInfo(form, 'template_ids'), inputs);
  return {
    mode,
    id: mode === 'one' ? id || (ids.length === 1 ? ids[0] : '') : '',
    ids: mode === 'multi' ? ids : [],
    cast: castOf(form, inputs),
    objects: objectsOf(form, inputs),
  };
}

/** Режим и выбор → правка ключей (как openOne и onOpenMulti/onOpenPack родной формы) */
export function selectionPatch(form: StudioForm, view: Pick<ReelsView, 'mode' | 'id' | 'ids'>): Record<string, Patch> {
  const idField = !!keyInfo(form, 'template_id');
  switch (view.mode) {
    case 'one':
      return idField
        ? { template_id: view.id || null, template_ids: null, mode: offers(form, 'one') }
        : { template_ids: view.id ? [view.id] : null, mode: offers(form, 'one') };
    case 'multi':
      return { template_ids: view.ids.length ? view.ids : null, template_id: null, mode: offers(form, 'multi') };
    case 'pack':
      return { mode: offers(form, 'pack'), template_id: null, template_ids: null };
    default:
      return { mode: offers(form, 'app'), template_id: null, template_ids: null };
  }
}
/** Состав → правка ключей: JSON `cast` (templates — только у набора) или поля `cast_<роль>`; ссылки в формате состава */
export function castPatch(form: StudioForm, lines: CastLine[]): Record<string, Patch> {
  const info = keyInfo(form, JSON_CAST);
  if (info?.json) {
    const skins = castSkins(form);
    const entries = lines.flatMap(line => {
      const entry = refEntry(line.role, line.ref, line.templates);
      if (!entry) return [];
      return skins && entry.who === 'char' && skinOf(line.skin) ? [{ ...entry, skin_id: line.skin }] : [entry];
    });
    return { [JSON_CAST]: entries.length ? (entries as unknown[]) : null };
  }
  const patch: Record<string, Patch> = {};
  for (const field of form.fields) {
    const m = CAST_FIELD.exec(field.key);
    if (!m || field.type !== 'cast') continue;
    const refs = [...new Set(lines.filter(line => line.role === m[1] && refEntry(line.role, line.ref)).map(line => line.ref))];
    patch[field.key] = refs.length ? refs.join(',') : null;
  }
  return patch;
}
/** Фото предмета вида kind → новый `objects` (JSON) — null, если вид пуст и других нет */
export function objectsPatch(form: StudioForm, inputs: Inputs, kind: string, ids: string[]): Record<string, Patch> {
  const info = keyInfo(form, 'objects');
  if (!info?.json) return {};
  const raw = { ...record(readKey(info, inputs)) };
  if (ids.length) raw[kind] = ids;
  else delete raw[kind];
  return { objects: Object.keys(raw).length ? raw : null };
}

/** Правка входов только опубликованными ключами (form-keys patchInputs; значения JSON-ключей — объекты и списки) */
export function applyPatch(form: StudioForm, prev: Inputs, patch: Record<string, Patch>): Inputs {
  return patchInputs(form, prev, patch as unknown as Parameters<typeof patchInputs>[2]);
}
/** Входы по виду поверх prev: режим, выбор, состав, фото предмета JSON-ключа (поля фото пишет мост загрузок) */
export function inputsOf(form: StudioForm, view: ReelsView, prev: Inputs = {}): Inputs {
  let next = applyPatch(form, prev, { ...selectionPatch(form, view), ...castPatch(form, view.cast) });
  if (keyInfo(form, 'objects')?.json) {
    const objects = Object.fromEntries(Object.entries(view.objects).filter(([, ids]) => ids.length));
    next = applyPatch(form, next, { objects: Object.keys(objects).length ? objects : null });
  }
  return next;
}
/** Ключи входов, которые пишет мост загрузок (id файлов): сверка «входы сменились не зеркалом» их не сравнивает */
export function uploadKeys(form: StudioForm | null | undefined): string[] {
  return [...(keyInfo(form, 'objects')?.json ? ['objects'] : []), ...Object.values(objectFields(form)).flat()];
}
